// AGC: CONAB Price Service - frontend API client
// AGC: Calls backend /api/v1/conab/prices to query Brazilian government commodity prices
// AGC: Data source: CONAB (Companhia Nacional de Abastecimento)

import { getBackendUrl } from '../utils/backendUrl'

const BASE_URL = getBackendUrl()

// AGC: Canonical crop enum matching backend
export type DroidfarmCrop =
  | 'soja'
  | 'milho'
  | 'cafe'
  | 'cana'
  | 'trigo'
  | 'arroz'
  | 'feijao'
  | 'laranja'
  | 'cacau'
  | 'algodao'
  | 'batata'
  | 'mandioca'

// AGC: Dataset identifiers
export type ConabDataset =
  | 'semanal_municipio'
  | 'mensal_municipio'
  | 'semanal_uf'
  | 'mensal_uf'

// AGC: Query parameters
export interface ConabQueryParams {
  dataset: ConabDataset
  product?: DroidfarmCrop
  uf?: string
  municipio?: string
  limit?: number
  debug?: boolean
}

// AGC: A single price row from the DTO
export interface ConabPriceRow {
  product: DroidfarmCrop | null
  uf?: string
  municipio?: string
  date?: string
  price: number | null
  unit?: string
  raw?: Record<string, string>
}

// AGC: Column mapping metadata
export interface ConabColumnMeta {
  product: string | null
  uf: string | null
  municipio: string | null
  date: string | null
  price: string | null
  unit: string | null
}

// AGC: Full response DTO
export interface ConabPriceResponse {
  dataset: string
  count: number
  totalMatches?: number
  meta: {
    etag?: string
    lastFetchedAt: number
    lastFetchedAtISO?: string
    sha256: string
    columns?: ConabColumnMeta
  }
  rows: ConabPriceRow[]
  error?: string
}

// AGC: Meta response for /api/v1/conab/meta
export interface ConabMetaResponse {
  datasets: string[]
  products: string[]
  description: string
  source: string
}

/**
 * AGC: Query CONAB prices from the backend
 * @param params - query parameters
 * @returns ConabPriceResponse
 */
export async function getConabPrices(params: ConabQueryParams): Promise<ConabPriceResponse> {
  const searchParams = new URLSearchParams()
  searchParams.set('dataset', params.dataset)
  if (params.product) searchParams.set('product', params.product)
  if (params.uf) searchParams.set('uf', params.uf)
  if (params.municipio) searchParams.set('municipio', params.municipio)
  if (params.limit) searchParams.set('limit', String(params.limit))
  if (params.debug) searchParams.set('debug', 'true')

  const response = await fetch(`${BASE_URL}/api/v1/conab/prices?${searchParams.toString()}`)

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `HTTP ${response.status}`)
  }

  return response.json()
}

/**
 * AGC: Get CONAB metadata (valid datasets and products)
 * @returns ConabMetaResponse
 */
export async function getConabMeta(): Promise<ConabMetaResponse> {
  const response = await fetch(`${BASE_URL}/api/v1/conab/meta`)
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`)
  }
  return response.json()
}
